import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization/localize";
import { formatMoney, RoundingStrategy } from "@faire/web--source/common/money";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";

export enum OwFFormFieldType {
  INPUT,
  NUMBER,
  DROPDOWN,
  RADIO,
  TEXTAREA,
  FILE,
  CHECKBOX,
  RESIDENTIAL_ADDRESS_AUTOCOMPLETE,
  STORE_ADDRESS_AUTOCOMPLETE,
  ADDRESS_FORM,
  MONEY,
  MONEY_RANGE_DROPDOWN,
  NESTED_CHECKBOXES,
  CREDIT_CHECK_AUTH,
  US_DATE,
}

export const OPEN_WITH_FAIRE_APPLICATION_DATA_KEY =
  "OPEN_WITH_FAIRE_APPLICATION";

/**
 * These field names don't actually have a correspending field in IRetailerLaunchWithFaire. They exist only for rendering purposes.
 * */
export const PSEUDO_FIELD_NAMES = ["popup_location_checkboxes"] as const;

/**
 * This is a key of IRetailerLaunchWithFaireApplication
 */
export type OwFApplicationKey = keyof IRetailerLaunchWithFaireApplication;

/**
 * This key can be either a key of IRetailerLaunchWithFaire or a pseudo field name for rendering purposes.
 */
export type OwFFormFieldKey =
  | OwFApplicationKey
  | (typeof PSEUDO_FIELD_NAMES)[number];

export type OwFFormErrors = Partial<{ [key in OwFFormFieldKey]: string }> & {
  message?: string | React.ReactNode;
  emailToken?: string;
};

export const LeaseSignedOptions = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: strictLocalize({
      defaultMessage: "Yes",
      description: { text: "Used as answer to question, like Yes/No" },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseSigned.YES,
  },
  {
    label: strictLocalize({
      defaultMessage: "No, I own my store",
      description: {
        text: "Used as answer to question, means retailer does not have a lease since they own the store",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseSigned.OWNED,
  },
  {
    label: strictLocalize({
      defaultMessage: "No, not yet",
      description: {
        text: "Used as answer to question, not yet means retailer has not yet signed a lease for store",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseSigned.NO,
  },
];

export const LeaseLengthOptions = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: strictLocalize({
      defaultMessage: "0 – 6 months",
      description: {
        text: "Used as answer to question, representing the length for lease of the store that retailer has",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseLength.ZERO_SIX_MONTHS,
  },
  {
    label: strictLocalize({
      defaultMessage: "6 – 12 months",
      description: {
        text: "Used as answer to question, representing the length for lease of the store that retailer has",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseLength.SIX_TWELVE_MONTHS,
  },
  {
    label: strictLocalize({
      defaultMessage: "1 – 2 years",
      description: {
        text: "Used as answer to question, representing the length for lease of the store that retailer has",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseLength.ONE_TWO_YEARS,
  },
  {
    label: strictLocalize({
      defaultMessage: "2 – 4 years",
      description: {
        text: "Used as answer to question, representing the length for lease of the store that retailer has",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseLength.TWO_FOUR_YEARS,
  },
  {
    label: strictLocalize({
      defaultMessage: "More than 4 years",
      description: {
        text: "Used as answer to question, representing the length for lease of the store that retailer has",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.LeaseLength.OVER_FOUR_YEARS,
  },
];

export const useOnlineHasStoreWebsiteOptions = () => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      label: strictLocalize({
        defaultMessage: "Yes",
        description: {
          text: "Anwser to question 'Do you have a live website link you can provide?'",
        },
      }),
      value: IRetailerLaunchWithFaireApplication.HasStoreWebsite.HAS_WEBSITE,
    },
    {
      label: strictLocalize({
        defaultMessage: "No, not yet",
        description: {
          text: "Anwser to question 'Do you have a live website link you can provide?'",
        },
      }),
      value: IRetailerLaunchWithFaireApplication.HasStoreWebsite.NO_WEBSITE,
    },
  ];
};

export const OnlineStoreCommitment = (
  strictLocalize: StrictLocalizeFunction
) => {
  return [
    {
      label: strictLocalize({
        defaultMessage: "0 - 8 hours per week",
        description: { text: "Time devoted to the online store each week" },
      }),
      value:
        IRetailerLaunchWithFaireApplication.StoreHoursCommitment
          .TIME_RANGE_0H_8H,
    },
    {
      label: strictLocalize({
        defaultMessage: "8 - 24 hours per week",
        description: { text: "Time devoted to the online store each week" },
      }),
      value:
        IRetailerLaunchWithFaireApplication.StoreHoursCommitment
          .TIME_RANGE_8H_24H,
    },
    {
      label: strictLocalize({
        defaultMessage: "24 - 40 hours per week",
        description: { text: "Time devoted to the online store each week" },
      }),
      value:
        IRetailerLaunchWithFaireApplication.StoreHoursCommitment
          .TIME_RANGE_24H_40H,
    },
    {
      label: strictLocalize({
        defaultMessage: "Over 40 hours per week",
        description: { text: "Time devoted to the online store each week" },
      }),
      value:
        IRetailerLaunchWithFaireApplication.StoreHoursCommitment.TIME_OVER_40H,
    },
  ];
};

export const PopUpLeaseOptions = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: strictLocalize({
      defaultMessage: "Yes",
      description: { text: "Used as answer to question, like Yes/No" },
    }),
    value: IRetailerLaunchWithFaireApplication.PopUpLease.YES_LEASE,
  },
  {
    label: strictLocalize({
      defaultMessage: "No, not yet",
      description: {
        text: "Used as answer to question. Retailer hasn't get a lease for new store secured yet",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.PopUpLease.NO_LEASE,
  },
  {
    label: strictLocalize({
      defaultMessage: "No, my pop-up does not require a lease",
      description: {
        text: "Used as answer to question. Retailer doesn't need a lease for new store",
      },
    }),
    value: IRetailerLaunchWithFaireApplication.PopUpLease.LEASE_NOT_REQUIRED,
  },
];

export const PopUpOccupyLengthOptions = (
  strictLocalize: StrictLocalizeFunction
) => [
  {
    label: strictLocalize({
      defaultMessage: "0 - 1 month",
      description: {
        text: "Used as answer to question. Represents a time span",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpMonthsOpen
        .POPUP_MONTHS_RANGE_0_1,
  },
  {
    label: strictLocalize({
      defaultMessage: "1 - 3 months",
      description: {
        text: "Used as answer to question. Represents a time span",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpMonthsOpen
        .POPUP_MONTHS_RANGE_1_3,
  },
  {
    label: strictLocalize({
      defaultMessage: "3 - 6 months",
      description: {
        text: "Used as answer to question. Represents a time span",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpMonthsOpen
        .POPUP_MONTHS_RANGE_3_6,
  },
  {
    label: strictLocalize({
      defaultMessage: "6 - 12 months",
      description: {
        text: "Used as answer to question. Represents a time span",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpMonthsOpen
        .POPUP_MONTHS_RANGE_6_12,
  },
  {
    label: strictLocalize({
      defaultMessage: "More than 1 year",
      description: {
        text: "Used as answer to question. Represents a time span",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpMonthsOpen.POPUP_MONTHS_OVER_12,
  },
];

export const PopUpDaysPerWeek = (strictLocalize: StrictLocalizeFunction) =>
  [...Array(8).keys()].slice(1).map((i) => ({
    label: strictLocalize(
      {
        defaultMessage:
          "{numOfDays, plural, one{# day a week} other{# days a week}}",
        description: {
          text: "Number of days per week. e.g. 1 day a week, 2 days a week",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        numOfDays: i,
      })
    ),
    value: i.toString(),
  }));

export const PopUpHoursOptions = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: strictLocalize({
      defaultMessage: "Less than 2 hours",
      description: {
        text: "Used as answer to question. Represents how long a shop opens each day",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpHoursOpen.POPUP_HOURS_UNDER_2,
  },
  {
    label: strictLocalize({
      defaultMessage: "2 - 4 hours",
      description: {
        text: "Used as answer to question. Represents how long a shop opens each day",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpHoursOpen.POPUP_HOURS_RANGE_2_4,
  },
  {
    label: strictLocalize({
      defaultMessage: "4 - 8 hours",
      description: {
        text: "Used as answer to question. Represents how long a shop opens each day",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpHoursOpen.POPUP_HOURS_RANGE_4_8,
  },
  {
    label: strictLocalize({
      defaultMessage: "8 - 12 hours",
      description: {
        text: "Used as answer to question. Represents how long a shop opens each day",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpHoursOpen.POPUP_HOURS_RANGE_8_12,
  },
  {
    label: strictLocalize({
      defaultMessage: "Over 12 hours",
      description: {
        text: "Used as answer to question. Represents how long a shop opens each day",
      },
    }),
    value:
      IRetailerLaunchWithFaireApplication.PopUpHoursOpen.POPUP_HOURS_OVER_12,
  },
];

export const getBrickAndMortarMonthlyRentText = (
  isLeaseSigned: boolean,
  currency: string,
  strictLocalize: StrictLocalizeFunction
) => {
  if (isLeaseSigned) {
    return {
      labelText: strictLocalize({
        defaultMessage: "What’s the monthly rent payment?",
        description: { text: "Question in Open with Faire application" },
      }),
    };
  }

  return {
    labelText: strictLocalize({
      defaultMessage: "What’s the monthly mortgage payment?",
      description: { text: "Question in Open with Faire application" },
    }),
    explanation: strictLocalize(
      {
        defaultMessage: "Enter {zeroMoney} if you have paid off your mortgage.",
        description: {
          text: "Explaining what to fill in if retailer does not pay mortgage any more",
        },
      },
      {
        zeroMoney: formatMoney({
          money: IFaireMoney.build({
            amount_cents: 0,
            currency: currency,
          }),
          roundingStrategy: RoundingStrategy.DROP_CENTS,
        }),
      }
    ),
  };
};

export const getNonBMMonthlyRentText = (
  strictLocalize: StrictLocalizeFunction,
  currency: string
) => ({
  [IRetailerLaunchWithFaireApplication.PopUpLease.YES_LEASE]: {
    labelText: strictLocalize({
      defaultMessage: "What’s the monthly rent payment?",
      description: { text: "Question in Open with Faire application" },
    }),
    explanation: strictLocalize({
      defaultMessage:
        "For multiple leases, enter the average monthly rent payment.",
      description: {
        text: "Explains what to fill in if retailers has more than one lease for their store",
      },
    }),
  },
  [IRetailerLaunchWithFaireApplication.PopUpLease.NO_LEASE]: {
    labelText: strictLocalize({
      defaultMessage: "What’s the expected monthly rent payment?",
      description: { text: "Question in Open with Faire application" },
    }),
    explanation: strictLocalize({
      defaultMessage:
        "For multiple leases, enter the average monthly rent payment.",
      description: {
        text: "Explains what to fill in if retailers has more than one lease for their store",
      },
    }),
  },
  [IRetailerLaunchWithFaireApplication.PopUpLease.LEASE_NOT_REQUIRED]: {
    labelText: strictLocalize({
      defaultMessage: "What’s the cost of occupying your space(s)?",
      description: { text: "Question in Open with Faire application" },
    }),
    explanation: strictLocalize(
      {
        defaultMessage:
          "E.g. {hundred}/day, {threeHundred} for a weekend, etc.",
        description: {
          text: "Explains how to fill in a question in application for cost",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        hundred: formatMoney({
          money: IFaireMoney.build({
            amount_cents: 10000,
            currency: currency,
          }),
          roundingStrategy: RoundingStrategy.DROP_CENTS,
        }),
        threeHundred: formatMoney({
          money: IFaireMoney.build({
            amount_cents: 300000,
            currency: currency,
          }),
          roundingStrategy: RoundingStrategy.DROP_CENTS,
        }),
      })
    ),
  },
});

export const BestStoreType = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: strictLocalize({
      defaultMessage: "Brick and mortar",
      description: {
        text: "Indicating retailer business type is brick-and-mortar",
      },
    }),
    value: IRetailer.BusinessCategory.BRICK_AND_MORTAR,
  },
  {
    label: strictLocalize({
      defaultMessage: "Pop-up shop",
      description: {
        text: "Indicating retailer business type is pop-up store",
      },
    }),
    value: IRetailer.BusinessCategory.POP_UP,
  },
  {
    label: strictLocalize({
      defaultMessage: "Online only",
      description: { text: "Indicating retailer business type is online-only" },
    }),
    value: IRetailer.BusinessCategory.ONLINE_ONLY,
  },
];

const FicoScore = IRetailerLaunchWithFaireApplication.FicoScore;
export const FICO_SCORE_OPTIONS = (strictLocalize: StrictLocalizeFunction) => [
  {
    value: FicoScore.FICO_SCORE_NONE,
    label: strictLocalize({
      defaultMessage: "No FICO",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.UNDER_500,
    label: strictLocalize({
      defaultMessage: "Under 500",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.BETWEEN_500_TO_600,
    label: strictLocalize({
      defaultMessage: "500-600",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.BETWEEN_600_TO_650,
    label: strictLocalize({
      defaultMessage: "601-650",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.BETWEEN_650_TO_700,
    label: strictLocalize({
      defaultMessage: "651-700",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.BETWEEN_700_TO_750,
    label: strictLocalize({
      defaultMessage: "701-750",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
  {
    value: FicoScore.OVER_750,
    label: strictLocalize({
      defaultMessage: "Over 750",
      description: {
        text: "Dropdown option for the Fair Isaac Corporation (FICO) credit score metric",
        img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
      },
    }),
  },
];

export const DefaultRequiredStoreFieldKeys: OwFFormFieldKey[] = [
  "store_name",
  "opening_range",
];

export const HighlightErrorFieldKeys: OwFFormFieldKey[] = ["funding_sources"];

export const ALREADY_OPEN_VALUE = -1;
